// The mixer surface (P4.10) — FR-14, drawn against UX §3.1's layout.
//
// §3.1 puts the mixer between the decks: channel columns of `trim / HI / MID / LOW / FILT / cue /
// fader`, a master with `gain / lim● / VU`, and the crossfader beneath. This builds the controls that
// exist and **draws the space for the ones that do not yet**, which is P4.10's own instruction about
// the cue buttons — *"draw the space, not the control"* — applied to everything M4 has not reached.
//
// ## ⚠️ Why this arrived before P4.4–P4.9 instead of after
//
// The pack's §5 lists P4.10 as *"genuinely movable — could come earlier and would make every prompt
// after it easier to judge by ear; the trade is that it would then be rebuilt twice as controls
// land."* It was moved on 9 September for a reason the trade did not anticipate: **P4.2 and P4.3
// shipped with human checks that could not be performed at all.** Trim and the EQ had no controls, so
// "sweep the trim, listen for a click" and "kill each band, does it sound like an EQ or a hole" were
// owed and impossible. P4.4, P4.5 and P4.6 each end in the same kind of check, so the debt compounds
// once per prompt.
//
// Rebuilding a panel twice is cheap. Shipping four DSP stages nobody has ever heard is not.
//
// ## What is real here and what is a placeholder
//
// Real: **trim and the three EQ bands, per channel.** They publish `ControlEvent`s and reach the
// audio thread. Placeholders are marked `mixer__todo` and carry the prompt that fills them, so a
// reader can tell an unimplemented control from a broken one — and so this file cannot quietly grow
// a control that looks live and is not.
//
// Every control is an `<input type="range">`, which is P4.10's keyboard requirement satisfied by
// construction rather than by adding key handlers: arrows, Home and End all work, the element is
// focusable, and a screen reader already knows what a slider is. The same argument `PitchFader` makes.

import { type JSX } from 'react';

import { MIXER_CONTROL, MIXER_MASTER_CONTROL } from '../../audio/engine/control-mapping';
import type { ControlId, DeckId } from '../../platform/types/control';
import {
  CROSSFADER_CURVE,
  EQ_MAX_GAIN,
  TRIM_RANGE_DB,
  crossfaderGains,
  faderGainFromPosition,
  masterGainFromPosition,
  type CrossfaderCurve,
  filterFromPosition,
  dbFromGain,
  eqGainFromPosition,
  trimGainFromPosition,
} from '../../platform/types/mixer';
import { METER_UNITY_FRACTION } from '../../platform/types/meter';
import {
  echoFeedbackFromPosition,
  FX_DIVISION,
  FX_DIVISIONS,
  FX_UNIT,
  fxDivisionLabel,
  fxUnitFromCode,
  reverbDecayFromPosition,
  type FxUnitKind,
} from '../../platform/types/fx';
import type { MixerApi, StripPositions } from './useMixer';
import type { MeterHandle, MetersApi } from './useMeters';

/**
 * One percent of travel per step.
 *
 * ⚠️ **Not `PitchFader`'s 0.0001, and driving the control by keyboard is what settled it.** That
 * value was chosen so "the fader's resolution is the pointer's, not the control's" — sound reasoning
 * about a pointer, and it forgets that `step` is also the *keyboard* increment. Measured in a real
 * browser: fifty presses of ArrowRight moved a knob by 0.005, half a percent of its travel. Crossing
 * the range would take five thousand presses.
 *
 * P4.10's verify asks that every control be "operable by keyboard and pointer through the same
 * path". Five thousand presses is operable in the letter and useless in fact.
 *
 * 0.01 gives a hundred presses end to end — two or three seconds on key repeat — and costs the
 * pointer nothing that matters: one percent of trim's ±12 dB is 0.24 dB, an order of magnitude below
 * what a hand is gain-staging to.
 *
 * ⚠️ `PitchFader` still carries 0.0001 and therefore still needs five thousand presses. That is P3.6
 * code and a different prompt; recorded in `BACKLOG-NOTES.md` under P4.10.
 */
const KNOB_STEP = 0.01;

/** `−∞` rather than a large negative number, because a killed band is not "very quiet". */
function formatDb(gain: number): string {
  const db = dbFromGain(gain);
  if (!Number.isFinite(db)) {
    return '−∞ dB';
  }
  const sign = db > 0.005 ? '+' : db < -0.005 ? '−' : '';
  return `${sign}${Math.abs(db).toFixed(1)} dB`;
}

interface KnobProps {
  readonly id: ControlId;
  readonly label: string;
  readonly position: number;
  readonly readout: string;
  readonly onMove: (id: ControlId, position: number) => void;
  /**
   * Travel per arrow press. Defaults to `KNOB_STEP`.
   *
   * ⚠️ Overridden only by controls with **discrete** positions — P4.8's division selector has four,
   * and a 1% step would need twenty-five presses to move between two of them while landing on
   * twenty-four positions that do not exist. The same keyboard argument `KNOB_STEP` was measured
   * for, arriving at the opposite number because the control is a different shape.
   */
  readonly step?: number;
}

function Knob({ id, label, position, readout, onMove, step }: KnobProps): JSX.Element {
  return (
    <label className="mixer__knob">
      <span className="micro">{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={step ?? KNOB_STEP}
        value={position}
        // `onChange`, not `onBlur` or `onMouseUp`: a mixer control that only spoke when a gesture
        // *ended* would be a control you cannot ride, which is the same reason `PitchFader` streams
        // every input event rather than waiting for `change`.
        onChange={(event) => {
          onMove(id, event.currentTarget.valueAsNumber);
        }}
        aria-label={label}
        aria-valuetext={readout}
      />
      <output className="mono mixer__readout">{readout}</output>
    </label>
  );
}

/**
 * What the filter knob is doing, in words.
 *
 * ⚠️ **"bypass" rather than a frequency at centre**, because the detent is a real bypass and printing
 * "16.0 kHz LP" there would say the filter was in the path when it is not. The readout is the only
 * place a user can tell the difference without listening, so it has to be the difference.
 */
function formatFilter(position: number): string {
  const setting = filterFromPosition(position);
  if (setting.mode === 'none') {
    return 'bypass';
  }
  const label = setting.mode === 'lowpass' ? 'LP' : 'HP';
  const hz = setting.cutoffHz;
  return hz >= 1000
    ? `${label} ${(hz / 1000).toFixed(1)} kHz`
    : `${label} ${hz.toFixed(0)} Hz`;
}

/**
 * Where the master gain sits, in dB relative to unity, with closed named rather than shown as −∞.
 */
function formatMasterGain(position: number): string {
  const gain = masterGainFromPosition(position);
  if (gain <= 0) {
    return 'closed';
  }
  const db = dbFromGain(gain);
  return `${db >= 0 ? '+' : ''}${db.toFixed(1)} dB`;
}

/**
 * What the fader is letting through, in dB, with silence named rather than shown as −∞.
 *
 * A number a user can act on: "−6.0 dB" is a position they can return to, where "0.42" is not.
 */
function formatFader(position: number): string {
  const gain = faderGainFromPosition(position);
  return gain <= 0 ? 'closed' : `${dbFromGain(gain).toFixed(1)} dB`;
}

/**
 * Which deck the crossfader is favouring, and by how much.
 *
 * ⚠️ Shows **both gains** rather than the position, because the position means different things
 * under the two curves: 0.15 is a gentle lean under `smooth` and fully open under `sharp`. A
 * readout that showed 0.15 for both would be describing the knob rather than the mix.
 */
function formatCrossfader(position: number, curve: CrossfaderCurve): string {
  const [a, b] = crossfaderGains(position, curve);
  const side = (gain: number): string =>
    gain <= 0 ? 'off' : dbFromGain(gain).toFixed(1);
  return `A ${side(a)} · B ${side(b)}`;
}

/**
 * What the FX division knob says, and the one place "free" reaches a user (P4.8).
 *
 * ⚠️ **"free" replaces the division rather than sitting beside it**, because on a gridless track the
 * division is not what is happening — the echo is running at its own fallback, and printing "1/2"
 * would be the readout asserting a sync that is not there. FR-04's loops make the same choice for
 * the same reason: unquantized, and *honestly so*.
 */
function formatDivision(position: number, hasGrid: boolean): string {
  if (!hasGrid) {
    return 'free';
  }
  const index = Math.min(
    FX_DIVISIONS.length - 1,
    Math.max(0, Math.round(position * (FX_DIVISIONS.length - 1))),
  );
  return fxDivisionLabel(FX_DIVISIONS[index] ?? FX_DIVISION.HALF);
}

/**
 * One unit button. Lit when its unit is loaded; clicking the lit one switches the slot off.
 *
 * A button rather than a slider stop, because the unit is a set and not a ramp — see the group's
 * comment. `aria-pressed` says exactly what this is; a range would need `aria-valuetext` to spell
 * out something a switch conveys by being one.
 */
function FxUnitButton({
  id,
  label,
  unit,
  selected,
  deck,
  onMove,
}: {
  readonly id: ControlId;
  readonly label: string;
  readonly unit: FxUnitKind;
  readonly selected: FxUnitKind;
  readonly deck: string;
  readonly onMove: (id: ControlId, position: number) => void;
}): JSX.Element {
  const active = selected === unit;
  return (
    <button
      type="button"
      className="mixer__fx-toggle"
      aria-pressed={active}
      aria-label={`${label} ${deck}`}
      onClick={() => {
        onMove(id, (active ? FX_UNIT.NONE : unit) / 2);
      }}
    >
      {label}
    </button>
  );
}

/** How dark the reverb's tail is, in words — a corner frequency means nothing on a knob. */
function formatDamping(position: number): string {
  if (position <= 0.05) {
    return 'bright';
  }
  if (position >= 0.95) {
    return 'dark';
  }
  return `${String(Math.round(position * 100))}%`;
}

/** The wet knob, as a percentage, with fully dry named rather than shown as "0%". */
function formatWet(position: number): string {
  return position <= 0 ? 'dry' : `${String(Math.round(position * 100))}%`;
}

/** A control UX §3.1 draws and M4 has not built. Visible, inert, and labelled with what fills it. */
function Reserved({ label, prompt }: { label: string; prompt: string }): JSX.Element {
  return (
    <div className="mixer__todo" aria-hidden="true">
      <span className="micro">{label}</span>
      <span className="micro mixer__todo-note">{prompt}</span>
    </div>
  );
}

/**
 * One level meter: an RMS bar, a peak marker, and a mark at 0 dBFS (P4.7).
 *
 * ⚠️ **Nothing here reads a value.** Both moving elements are written straight to the DOM by
 * `useMeters`'s rAF loop through these refs, which is Stack §2's rule — a meter driven by React
 * state would re-render this subtree sixty times a second.
 *
 * `aria-hidden`, and it is not laziness. A bar that changes 60 times a second is not something a
 * screen reader can usefully announce, and a live region here would talk continuously over the
 * music. The numbers a non-sighted user needs from the mixer are the *control* readouts, which are
 * real text on every knob beside this.
 */
function Meter({
  handle,
  label,
  name,
}: {
  handle: MeterHandle;
  label: string;
  /**
   * A stable hook for the e2e suite (P4.11).
   *
   * ⚠️ **`data-meter` rather than text or position, and this suite has already paid for the
   * alternative.** P4.10's keyboard test used `.mixer__readout`.last()` — "the last readout in
   * channel A" — and P4.4 adding the filter knob below the EQ silently changed which control that
   * meant. A selector that depends on how many controls a column has breaks every time the column
   * grows, which it has done four times this milestone. These meters are `aria-hidden`, so there is
   * no accessible name to select by either.
   */
  name: string;
}): JSX.Element {
  return (
    <div className="mixer__meter" data-meter={name} aria-hidden="true">
      <span className="micro">{label}</span>
      <div className="bar bar--meter mixer__meter-track">
        <div className="bar__fill" ref={handle.rms} />
        {/* The 0 dBFS mark. The scale runs to +6 so the master can *show* an overload — see
            `METER_TOP_DB`, and `MixerBus#measure` for why the tap is pre-limiter. */}
        <div
          className="mixer__meter-unity"
          style={{ left: `${(METER_UNITY_FRACTION * 100).toFixed(1)}%` }}
        />
        <div className="mixer__meter-peak" ref={handle.peak} />
      </div>
    </div>
  );
}

interface StripProps {
  readonly deck: DeckId;
  readonly positions: StripPositions;
  readonly onMove: (id: ControlId, position: number) => void;
  readonly meter: MeterHandle;
  /**
   * Whether this deck has a usable beatgrid (P4.8).
   *
   * ⚠️ **Read on the main thread rather than published back from the worklet, and that is the point
   * of `EchoTiming`'s comment.** The deck is the side that owns whether its tempo is trustworthy —
   * it is the side that detected it and the side FR-13 lets a human correct. Sending the kernel's
   * own `'beat' | 'free'` reading back up would be a second answer travelling the long way round.
   */
  readonly hasGrid: boolean;
}

function ChannelStripColumn({
  deck,
  positions,
  onMove,
  meter,
  hasGrid,
}: StripProps): JSX.Element {
  const control = MIXER_CONTROL[deck];
  const name = deck.toUpperCase();
  // The selector rides a 0…1 control, so the position is the code over two — see the mapping's
  // `toEngineValue`, which is the same arithmetic in the other direction.
  const selectedUnit = fxUnitFromCode(Math.round(positions.fxUnit * 2));

  return (
    <div className="mixer__strip" role="group" aria-label={`Channel ${name}`}>
      <span className="micro mixer__strip-name">{name}</span>

      <Knob
        id={control.TRIM}
        label={`Trim ${name}`}
        position={positions.trim}
        readout={formatDb(trimGainFromPosition(positions.trim))}
        onMove={onMove}
      />

      {/* HI, MID, LOW — top to bottom, matching UX §3.1's column and every hardware mixer. The
          array order is the *drawing* order and deliberately not the band-index order; `EQ_BAND`
          owns that, and the two are related by the control id rather than by position. */}
      <Knob
        id={control.EQ_HIGH}
        label={`High ${name}`}
        position={positions.eqHigh}
        readout={formatDb(eqGainFromPosition(positions.eqHigh, EQ_MAX_GAIN))}
        onMove={onMove}
      />
      <Knob
        id={control.EQ_MID}
        label={`Mid ${name}`}
        position={positions.eqMid}
        readout={formatDb(eqGainFromPosition(positions.eqMid, EQ_MAX_GAIN))}
        onMove={onMove}
      />
      <Knob
        id={control.EQ_LOW}
        label={`Low ${name}`}
        position={positions.eqLow}
        readout={formatDb(eqGainFromPosition(positions.eqLow, EQ_MAX_GAIN))}
        onMove={onMove}
      />

      {/* Centre is a detent and a real bypass, so the readout says "bypass" rather than a
          frequency — a number there would imply the filter was doing something. */}
      <Knob
        id={control.FILTER}
        label={`Filter ${name}`}
        position={positions.filter}
        readout={formatFilter(positions.filter)}
        onMove={onMove}
      />
      {/* P4.10's rule names this one explicitly: the cue buttons in §3.1's drawing are M5. */}
      <Reserved label="Cue" prompt="M5" />
      {/* FR-15's channel fader (P4.5). Linear in amplitude and reaching true silence at the
          bottom — `faderGainFromPosition` says why that differs from trim. */}
      <Knob
        id={control.FADER}
        label={`Fader ${name}`}
        position={positions.fader}
        readout={formatFader(positions.fader)}
        onMove={onMove}
      />
      {/* FR-14's pre-fader meter (P4.7). ⚠️ **Pre-fader, which is why it sits below the fader and
          does not move with it** — the tap is inside `ChannelStrip` after trim, EQ and filter and
          before the channel gain, so this answers "how hot is this channel" rather than "how much
          of it is currently reaching the mix". `STRIP_A`/`STRIP_B` carry it, and they are separate
          sources from `DECK_A`/`DECK_B` for exactly that reason. */}
      <Meter handle={meter} label="Meter" name={`strip-${deck}`} />

      {/* FR-17's FX slot (P4.8). Drawn below the fader because it is drawn *after* it in the chain
          only in the sense that a user reaches for it last — the signal path puts FX before the
          fader, which is what the pre-fader meter above is measuring. */}
      <div className="mixer__fx" role="group" aria-label={`FX ${name}`}>
        {/* ⚠️ **Two buttons rather than a three-position slider.** The unit is a *set* — off, echo,
            reverb — and a slider implies an ordering and an in-between that do not exist. Clicking
            the lit one switches it off, so "off" needs no button of its own. */}
        <div className="mixer__fx-units">
          <FxUnitButton
            id={control.FX_UNIT}
            label="ECHO"
            unit={FX_UNIT.ECHO}
            selected={selectedUnit}
            deck={name}
            onMove={onMove}
          />
          <FxUnitButton
            id={control.FX_UNIT}
            label="VERB"
            unit={FX_UNIT.REVERB}
            selected={selectedUnit}
            deck={name}
            onMove={onMove}
          />
        </div>
        {/* The two knobs mean different things per unit, so they are different controls with their
            own ids and their own remembered positions — switching unit and back finds the echo
            exactly where it was left, which a shared pair of knobs could not do. */}
        {selectedUnit === FX_UNIT.REVERB ? (
          <>
            <Knob
              id={control.FX_DECAY}
              label={`FX decay ${name}`}
              position={positions.fxDecay}
              readout={`${reverbDecayFromPosition(positions.fxDecay).toFixed(1)} s`}
              onMove={onMove}
            />
            <Knob
              id={control.FX_DAMPING}
              label={`FX damping ${name}`}
              position={positions.fxDamping}
              readout={formatDamping(positions.fxDamping)}
              onMove={onMove}
            />
          </>
        ) : (
          <>
            <Knob
              id={control.FX_DIVISION}
              label={`FX division ${name}`}
              position={positions.fxDivision}
              step={1 / (FX_DIVISIONS.length - 1)}
              readout={formatDivision(positions.fxDivision, hasGrid)}
              onMove={onMove}
            />
            <Knob
              id={control.FX_FEEDBACK}
              label={`FX feedback ${name}`}
              position={positions.fxFeedback}
              readout={`${String(Math.round(echoFeedbackFromPosition(positions.fxFeedback) * 100))}%`}
              onMove={onMove}
            />
          </>
        )}
        <Knob
          id={control.FX_MIX}
          label={`FX wet ${name}`}
          position={positions.fxMix}
          readout={formatWet(positions.fxMix)}
          onMove={onMove}
        />
      </div>
    </div>
  );
}

export interface MixerPanelProps {
  readonly mixer: MixerApi;
  /** The meters' refs (P4.7). Values never pass through here — see `useMeters`. */
  readonly meters: MetersApi;
  /** Which decks have a usable beatgrid (P4.8) — see `StripProps.hasGrid`. */
  readonly hasGrid: Record<DeckId, boolean>;
}

export function MixerPanel({ mixer, meters, hasGrid }: MixerPanelProps): JSX.Element {
  return (
    <div className="mixer" role="group" aria-label="Mixer">
      <div className="mixer__channels">
        <ChannelStripColumn
          deck="a"
          positions={mixer.positions.a}
          onMove={mixer.move}
          meter={meters.a}
          hasGrid={hasGrid.a}
        />
        <div className="mixer__master" role="group" aria-label="Master">
          <span className="micro mixer__strip-name">MST</span>
          {/* FR-16's master gain (P4.6), before the limiter so turning it up drives the
              brickwall harder rather than undoing it — see `limiter.ts`. */}
          <Knob
            id={MIXER_MASTER_CONTROL.MASTER_GAIN}
            label="Master gain"
            position={mixer.masterGain}
            readout={formatMasterGain(mixer.masterGain)}
            onMove={mixer.move}
          />
          {/* ⚠️ The limiter has **no control**, and the empty slot is the statement: FR-16 says
              always-on and `limiter.ts` explains why a bypass would remove the stage that makes
              the gain staging legal. What belongs here is its *gain reduction*, which the bus
              measures today and P4.7 publishes through the FR-26 frame. */}
          <div className="mixer__meter" data-meter="gain-reduction" aria-hidden="true">
            <span className="micro">Limiter GR</span>
            <div className="bar mixer__meter-track">
              {/* Its own scale, growing rightwards from no reduction — see `GAIN_REDUCTION_MAX_DB`
                  for why it stops at 12 dB rather than continuing. */}
              <div
                className="bar__fill bar__fill--reduction"
                ref={meters.gainReduction}
              />
            </div>
          </div>
          {/* FR-16's master meter (P4.7), read **pre-limiter**. The pair is the instrument: this
              says how hard the bus is being driven, the bar above says what the limiter took off to
              make that legal. Either number alone is close to unreadable. */}
          <Meter handle={meters.master} label="VU" name="master" />
        </div>
        <ChannelStripColumn
          deck="b"
          positions={mixer.positions.b}
          onMove={mixer.move}
          meter={meters.b}
          hasGrid={hasGrid.b}
        />
      </div>

      {/* FR-15's crossfader and its two curves (P4.5). One control, two instruments — see
          `crossfaderGains`. */}
      <div className="mixer__crossfader" role="group" aria-label="Crossfader">
        <Knob
          id={MIXER_MASTER_CONTROL.CROSSFADER}
          label="Crossfader"
          position={mixer.crossfader}
          readout={formatCrossfader(mixer.crossfader, mixer.crossfaderCurve)}
          onMove={mixer.move}
        />
        {/* A switch on the same 0…1 bus as everything else, so FF-1's MIDI learn can bind it
            like any other control rather than needing a second kind of event. */}
        <Knob
          id={MIXER_MASTER_CONTROL.CROSSFADER_CURVE}
          label="Crossfader curve"
          position={mixer.crossfaderCurve === CROSSFADER_CURVE.SHARP ? 1 : 0}
          readout={mixer.crossfaderCurve === CROSSFADER_CURVE.SHARP ? 'sharp' : 'smooth'}
          onMove={mixer.move}
        />
      </div>

      <span className="micro mixer__note">
        Trim ±{TRIM_RANGE_DB} dB · EQ kills at minimum · filter centre is bypass
      </span>
    </div>
  );
}
